// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Fiscal/NotaFiscalDetalhe.tsx
//  Detalhe completo de uma NF-e: cabeçalho, chave, protocolo e eventos.
// ════════════════════════════════════════════════════════════════════════════

import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, XCircle, RefreshCw, CheckCircle2, Clock, AlertCircle, Ban, Download, Printer, Trash2, AlertTriangle, Pencil } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import { notaFiscalService } from '@/features/fiscal/services/notaFiscalService';
import { ConfirmarTransmissaoDialog } from '@/features/fiscal/components/ConfirmarTransmissaoDialog';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useState } from 'react';
import { useNotaFiscalDetalhe, useNotaFiscal } from '@/features/fiscal/hooks/useNotaFiscal';
import { statusDeCStat } from '@/features/fiscal/types';
import type { NotaFiscalEventoDto } from '@/features/fiscal/types';

// ── helpers ──────────────────────────────────────────────────────────────────

const fmtDate = (s: string) =>
  new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });

const fmtDateOnly = (s: string) =>
  new Date(s).toLocaleDateString('pt-BR');

const statusConfig = {
  emitida:   { label: 'Autorizada',   icon: CheckCircle2, color: 'text-success'      },
  cancelada: { label: 'Cancelada',    icon: Ban,          color: 'text-destructive'   },
  denegada:  { label: 'Denegada',     icon: AlertCircle,  color: 'text-warning'       },
  pendente:  { label: 'Pendente',     icon: Clock,        color: 'text-muted-foreground' },
  inutilizada: { label: 'Inutilizada', icon: Ban,         color: 'text-muted-foreground' },
};

const fmtMoeda = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtQtd   = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 4 });

/** Item sem o mínimo fiscal: a SEFAZ rejeita a nota. */
const pendenciaFiscal = (i: { ncm: string | null; cfop: string | null; csosn: string | null; cst: string | null }) =>
  [!i.ncm && 'NCM', !i.cfop && 'CFOP', !i.csosn && !i.cst && 'CSOSN/CST'].filter(Boolean).join(', ');

const tipoDocLabel = (tipo: number | null) => {
  if (tipo === 55) return 'NF-e (55)';
  if (tipo === 65) return 'NFC-e (65)';
  return tipo != null ? String(tipo) : '—';
};

const finalidadeLabel = (id: number | null) => {
  const map: Record<number, string> = { 1: 'Normal', 2: 'Complementar', 3: 'Ajuste', 4: 'Devolução' };
  return id != null ? (map[id] ?? String(id)) : '—';
};

function tipoEventoLabel(tipo: number | null) {
  const map: Record<number, string> = {
    110111: 'Cancelamento',
    110110: 'Carta de Correção (CCe)',
    110140: 'EPEC',
    110150: 'Cancelamento por Substituição',
  };
  return tipo != null ? (map[tipo] ?? `Evento ${tipo}`) : 'Evento';
}

// ── Linha de info ─────────────────────────────────────────────────────────────

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium break-all">{value ?? '—'}</p>
    </div>
  );
}

// ── Timeline de eventos ───────────────────────────────────────────────────────

function EventoItem({ evento, onBaixarXml, onImprimir }: {
  evento: NotaFiscalEventoDto; onBaixarXml?: () => void; onImprimir?: () => void;
}) {
  const ehCartaCorrecao = evento.tipoEvento === 110110;
  return (
    <div className="flex gap-3">
      <div className="flex flex-col items-center">
        <div className="h-2 w-2 rounded-full bg-primary mt-1.5" />
        <div className="flex-1 w-px bg-border" />
      </div>
      <div className="pb-4 space-y-0.5">
        <p className="text-sm font-medium">{tipoEventoLabel(evento.tipoEvento)}</p>
        <p className="text-xs text-muted-foreground">{fmtDate(evento.dtSituacao)}</p>
        {evento.cStat && (
          <p className="text-xs text-muted-foreground">
            cStat: <span className="font-mono">{evento.cStat}</span> — {evento.xMotivo}
          </p>
        )}
        {/* Sem o "? :" o React imprime o próprio 0 quando o protocolo vem zerado. */}
        {evento.protocolo ? (
          <p className="text-xs text-muted-foreground">
            Protocolo: <span className="font-mono">{evento.protocolo}</span>
          </p>
        ) : null}
        {evento.texto ? (
          <p className="text-sm whitespace-pre-wrap pt-1">
            <span className="text-xs text-muted-foreground">Correção: </span>{evento.texto}
          </p>
        ) : null}
        {evento.temXml ? (
          <div className="flex flex-wrap gap-2 pt-1">
            {ehCartaCorrecao && onImprimir ? (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={onImprimir}>
                <Printer className="h-3.5 w-3.5" /> Imprimir carta
              </Button>
            ) : null}
            {onBaixarXml ? (
              <Button variant="outline" size="sm" className="gap-1.5" onClick={onBaixarXml}>
                <Download className="h-3.5 w-3.5" /> Baixar XML do evento
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
//  Página Principal
// ══════════════════════════════════════════════════════════════════════════════

export default function NotaFiscalDetalhe() {
  const { id }    = useParams<{ id: string }>();
  const navigate  = useNavigate();
  const notaId    = Number(id ?? 0);

  const { data: nota, isLoading } = useNotaFiscalDetalhe(notaId);

  // Reusa o hook de lista apenas para as mutations de transmitir/cancelar
  const { transmitir, cancelar, isTransmitindo, isCancelando } = useNotaFiscal(1, 1, {}, false);

  const [justificativa, setJustificativa] = useState('');
  const [cancelDialog, setCancelDialog]   = useState(false);
  const [confirmarTransmissao, setConfirmarTransmissao] = useState(false);
  // Cancelar a nota de um pedido estorna o pedido junto, salvo se o usuário desmarcar
  // (caso em que a venda continua valendo e a nota só será emitida de novo).
  const [estornarPedido, setEstornarPedido] = useState(true);
  // Carta de correção e inutilização: mesmo diálogo (texto + envio à SEFAZ), ações diferentes.
  const [evento, setEvento]           = useState<'cce' | 'inutilizar' | null>(null);
  const [textoEvento, setTextoEvento] = useState('');
  const [enviandoEvento, setEnviandoEvento] = useState(false);
  const [descartarDialog, setDescartarDialog] = useState(false);
  const [descartando, setDescartando]         = useState(false);
  const queryClient = useQueryClient();

  const { data: conteudo } = useQuery({
    queryKey: ['nota-fiscal-conteudo', notaId],
    queryFn:  () => notaFiscalService.getConteudo(notaId),
    enabled:  notaId > 0,
  });

  const [atualizando, setAtualizando] = useState(false);
  const handleAtualizarDoPedido = async () => {
    setAtualizando(true);
    try {
      const r = await notaFiscalService.atualizarDoPedido(notaId);
      if (r.avisos.length > 0) {
        toast.warning('Nota refeita pelo pedido — ainda há pendências.', { description: r.avisos.join(' • '), duration: 15000 });
      } else {
        toast.success('Nota refeita pelo pedido com o cadastro atual. Pode transmitir.');
      }
      void queryClient.invalidateQueries();
    } catch (err) {
      toast.error('Não foi possível atualizar a nota.', { description: getApiErrorMessage(err) });
    } finally {
      setAtualizando(false);
    }
  };

  const eventoCfg = evento === 'cce'
    ? { titulo: 'Carta de correção', max: 1000, rotulo: 'Texto da correção *', botao: 'Enviar carta de correção' }
    : { titulo: 'Inutilizar número', max: 255,  rotulo: 'Justificativa *',     botao: 'Inutilizar o número' };

  const handleEvento = async () => {
    const texto = textoEvento.trim();
    if (!evento || texto.length < 15) return;
    setEnviandoEvento(true);
    try {
      const r = evento === 'cce'
        ? await notaFiscalService.cartaCorrecao(notaId, texto)
        : await notaFiscalService.inutilizar(notaId, texto);
      if (r.sucesso) {
        toast.success(evento === 'cce' ? 'Carta de correção registrada na SEFAZ.' : 'Número inutilizado na SEFAZ.');
        setEvento(null);
        setTextoEvento('');
      } else {
        toast.warning(`SEFAZ: ${r.cStat} — ${r.xMotivo}`);
      }
      await queryClient.invalidateQueries({
        predicate: (q) => q.queryKey[0] === 'notas-fiscais' || q.queryKey[0] === 'nota-fiscal-conteudo',
      });
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Não foi possível concluir a operação.'));
    } finally {
      setEnviandoEvento(false);
    }
  };

  const handleDescartar = async () => {
    setDescartando(true);
    try {
      await notaFiscalService.descartarRascunho(notaId);
      toast.success('Rascunho descartado.', {
        description: conteudo?.pedido ? `O pedido nº ${conteudo.pedido} pode ser faturado de novo.` : undefined,
      });
      navigate('/fiscal/notas-fiscais');
      // Atualiza listas (notas, pedidos), mas não rebusca a nota que acabou de ser excluída (daria 404).
      void queryClient.invalidateQueries({
        predicate: (q) => !(q.queryKey[1] === notaId
          && (q.queryKey[0] === 'notas-fiscais' || q.queryKey[0] === 'nota-fiscal-conteudo')),
      });
    } catch (err) {
      toast.error('Não foi possível descartar o rascunho.', { description: getApiErrorMessage(err) });
    } finally {
      setDescartando(false);
      setDescartarDialog(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!nota) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">Nota fiscal não encontrada.</p>
        <Button variant="ghost" className="mt-4 gap-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" /> Voltar
        </Button>
      </div>
    );
  }

  const status = statusDeCStat(nota.cStat);
  const cfg    = statusConfig[status];
  const StatusIcon = cfg.icon;

  const handleCancelar = () => {
    if (justificativa.trim().length < 15) return;
    // O estorno do pedido só existe para nota gerada de pedido.
    cancelar({ id: notaId, req: { justificativa, estornarPedido: !!conteudo?.pedido && estornarPedido } });
    setCancelDialog(false);
    setJustificativa('');
  };

  return (
    <div className="p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              NF-e {String(nota.numeroNota).padStart(6, '0')}
              {nota.serie && <span className="text-muted-foreground font-normal"> / Série {nota.serie}</span>}
            </h1>
            <p className="text-sm text-muted-foreground">
              {nota.situacao === 'rascunho' ? 'Data de emissão' : 'Emitida em'}{' '}
              {nota.dtEmissao ? nota.dtEmissao.slice(0, 10).split('-').reverse().join('/') : fmtDateOnly(nota.dtCriacao)}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className={`flex items-center gap-1.5 ${cfg.color}`}>
            <StatusIcon className="h-5 w-5" />
            <span className="font-semibold">
              {nota.situacao === 'rejeitada' ? 'Rejeitada' : nota.situacao === 'rascunho' ? 'Rascunho' : cfg.label}
            </span>
          </div>
          <Badge variant="outline" className={nota.isAmbienteProducao ? '' : 'text-yellow-600 border-yellow-500'}>
            {nota.isAmbienteProducao ? 'Produção' : 'Homologação'}
          </Badge>
          {status === 'pendente' && (
            <Button size="sm" className="gap-1.5" onClick={() => setConfirmarTransmissao(true)} disabled={isTransmitindo}>
              {isTransmitindo ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Transmitir
            </Button>
          )}
          {conteudo?.podeEditar && (
            <Button size="sm" variant="outline" className="gap-1.5"
              onClick={() => navigate(`/fiscal/notas-fiscais/${nota.id}/editar`)}
              title="Reabre a nota para alterar destinatário, itens, transporte, pagamento e observações">
              <Pencil className="h-3.5 w-3.5" /> Editar
            </Button>
          )}
          {conteudo?.podeAtualizarDoPedido && (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={handleAtualizarDoPedido} disabled={atualizando}
              title="Refaz destinatário, itens e parcelas com o cadastro atual, mantendo o número da nota">
              <RefreshCw className={`h-3.5 w-3.5 ${atualizando ? 'animate-spin' : ''}`} /> Atualizar pelo pedido
            </Button>
          )}
          {conteudo?.podeDescartar && (
            <Button size="sm" variant="outline" className="gap-1.5 text-destructive"
              onClick={() => setDescartarDialog(true)} disabled={descartando}>
              <Trash2 className="h-3.5 w-3.5" /> {nota.numeroNota > 0 ? 'Descartar nota' : 'Descartar rascunho'}
            </Button>
          )}
          {nota.temXml && nota.tipoDocFiscal === 55 && (
            <Button size="sm" variant="outline" className="gap-1.5"
              onClick={() => notaFiscalService.abrirDanfe(notaId).catch((err) =>
                toast.error('Não foi possível gerar o DANFE.', { description: getApiErrorMessage(err) }))}>
              <Printer className="h-3.5 w-3.5" /> DANFE
            </Button>
          )}
          {nota.temXml && (
            <Button size="sm" variant="outline" className="gap-1.5"
              onClick={() => notaFiscalService.baixarXml(notaId, nota.chaveNota).catch((err) =>
                toast.error('Não foi possível baixar o XML.', { description: getApiErrorMessage(err) }))}>
              <Download className="h-3.5 w-3.5" /> XML
            </Button>
          )}
          {conteudo?.podeInutilizar && (
            <Button size="sm" variant="outline" className="gap-1.5 text-destructive"
              onClick={() => { setTextoEvento(''); setEvento('inutilizar'); }}>
              <Ban className="h-3.5 w-3.5" /> Inutilizar número
            </Button>
          )}
          {nota.isImportada && (
            <Badge variant="outline"
              title="Emitida em outro sistema e importada pelo XML: cancelamento e carta de correção são feitos no sistema que a emitiu">
              Importada
            </Badge>
          )}
          {status === 'emitida' && !nota.isImportada && (
            <Button size="sm" variant="outline" className="gap-1.5"
              onClick={() => { setTextoEvento(''); setEvento('cce'); }}>
              <Pencil className="h-3.5 w-3.5" /> Carta de correção
            </Button>
          )}
          {status === 'emitida' && !nota.isImportada && (
            <Button size="sm" variant="destructive" className="gap-1.5"
              onClick={() => setCancelDialog(true)} disabled={isCancelando}>
              <XCircle className="h-3.5 w-3.5" /> Cancelar
            </Button>
          )}
        </div>
      </div>

      {/* Dados SEFAZ */}
      {(nota.cStat || nota.protocolo) && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Retorno SEFAZ</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoRow label="cStat" value={nota.cStat && <span className="font-mono">{nota.cStat}</span>} />
            <InfoRow label="xMotivo" value={nota.xMotivo} />
            <InfoRow label="Protocolo" value={nota.protocolo && <span className="font-mono text-xs">{nota.protocolo}</span>} />
            {nota.protocoloCancelamento && (
              <InfoRow label="Protocolo Cancelamento" value={<span className="font-mono text-xs">{nota.protocoloCancelamento}</span>} />
            )}
          </CardContent>
        </Card>
      )}

      {/* Chave de Acesso */}
      {nota.chaveNota && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Chave de Acesso</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="font-mono text-sm break-all bg-muted rounded px-3 py-2 select-all">
              {nota.chaveNota}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Conteúdo da nota — o que se confere antes de transmitir */}
      {conteudo && (
        <>
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">Destinatário</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <InfoRow label="Nome / Razão social" value={conteudo.destinatario.nome} />
              <InfoRow label="CPF/CNPJ" value={conteudo.destinatario.cpfCnpj && <span className="font-mono">{conteudo.destinatario.cpfCnpj}</span>} />
              <InfoRow label="Inscrição estadual" value={conteudo.destinatario.inscricaoEstadual ?? (conteudo.destinatario.consumidorFinal ? 'Não contribuinte' : null)} />
              <InfoRow label="E-mail" value={conteudo.destinatario.email} />
              <InfoRow label="Endereço" value={[conteudo.destinatario.endereco, conteudo.destinatario.numero].filter(Boolean).join(', ') || null} />
              <InfoRow label="Bairro" value={conteudo.destinatario.bairro} />
              <InfoRow label="Cidade / UF" value={[conteudo.destinatario.cidade, conteudo.destinatario.uf].filter(Boolean).join(' / ') || null} />
              <InfoRow label="Código IBGE do município" value={conteudo.destinatario.cidadeIbge || (
                <span className="text-destructive">Não informado — a SEFAZ rejeita</span>
              )} />
              <InfoRow label="Natureza da operação" value={conteudo.naturezaOperacao} />
              {conteudo.pedido && <InfoRow label="Pedido de venda" value={`nº ${conteudo.pedido}`} />}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">
                Itens <Badge variant="secondary" className="ml-2">{conteudo.itens.length}</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Produto</TableHead>
                      <TableHead>NCM</TableHead>
                      <TableHead>CFOP</TableHead>
                      <TableHead>CSOSN/CST</TableHead>
                      <TableHead>Un.</TableHead>
                      <TableHead className="text-right">Qtd.</TableHead>
                      <TableHead className="text-right">Unitário</TableHead>
                      <TableHead className="text-right">Desconto</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {conteudo.itens.map((i) => {
                      const falta = pendenciaFiscal(i);
                      return (
                        <TableRow key={i.item}>
                          <TableCell>{i.item}</TableCell>
                          <TableCell>
                            {i.descricao ?? `Produto ${i.produtoId}`}
                            {falta && (
                              <p className="flex items-center gap-1 text-xs text-destructive">
                                <AlertTriangle className="h-3 w-3" /> Sem {falta} — corrija o cadastro do produto e use "Atualizar pelo pedido"
                              </p>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{i.ncm || '—'}</TableCell>
                          <TableCell className="font-mono text-xs">{i.cfop || '—'}</TableCell>
                          <TableCell className="font-mono text-xs">{i.csosn || i.cst || '—'}</TableCell>
                          <TableCell>{i.unidade ?? '—'}</TableCell>
                          <TableCell className="text-right">{fmtQtd(i.quantidade)}</TableCell>
                          <TableCell className="text-right">{fmtMoeda(i.valorUnitario)}</TableCell>
                          <TableCell className="text-right">{i.desconto ? fmtMoeda(i.desconto) : '—'}</TableCell>
                          <TableCell className="text-right font-medium">{fmtMoeda(i.valorTotal - i.desconto)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-3 flex flex-wrap justify-end gap-x-6 gap-y-1 text-sm">
                <span className="text-muted-foreground">Produtos: <b className="text-foreground">{fmtMoeda(conteudo.totais.produtos)}</b></span>
                {conteudo.totais.desconto > 0 && (
                  <span className="text-muted-foreground">Desconto: <b className="text-foreground">{fmtMoeda(conteudo.totais.desconto)}</b></span>
                )}
                {conteudo.totais.outrasDespesas > 0 && (
                  <span className="text-muted-foreground">Outras despesas: <b className="text-foreground">{fmtMoeda(conteudo.totais.outrasDespesas)}</b></span>
                )}
                <span className="text-muted-foreground">Total da nota: <b className="text-foreground">{fmtMoeda(conteudo.totais.nota)}</b></span>
              </div>
            </CardContent>
          </Card>

          {(conteudo.duplicatas.length > 0 || conteudo.pagamentos.length > 0 || conteudo.lotes.length > 0) && (
            <div className="grid gap-6 md:grid-cols-2">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Pagamento e duplicatas</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  {conteudo.pagamentos.map((p, idx) => (
                    <div key={`p${idx}`} className="flex justify-between">
                      <span>{p.forma ?? 'Outros'}</span><span>{fmtMoeda(p.valor)}</span>
                    </div>
                  ))}
                  {conteudo.duplicatas.map((d) => (
                    <div key={`d${d.parcela}`} className="flex justify-between text-muted-foreground">
                      <span>Duplicata {String(d.parcela).padStart(3, '0')} — vence {fmtDateOnly(d.vencimento)}</span>
                      <span>{fmtMoeda(d.valor)}</span>
                    </div>
                  ))}
                  {conteudo.duplicatas.length === 0 && conteudo.pagamentos.length === 0 && (
                    <p className="text-muted-foreground">Sem pagamento informado.</p>
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm">Lotes (rastreabilidade)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-1 text-sm">
                  {conteudo.lotes.length === 0 ? (
                    <p className="text-muted-foreground">Nenhum lote vinculado.</p>
                  ) : conteudo.lotes.map((l, idx) => (
                    <div key={idx} className="flex justify-between">
                      <span>
                        Lote <span className="font-mono">{l.lote}</span>
                        {l.validade && <span className="text-muted-foreground"> — validade {fmtDateOnly(l.validade)}</span>}
                      </span>
                      <span>{fmtQtd(l.quantidade)}</span>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </>
      )}

      {/* Informações da Nota */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Informações da Nota</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <InfoRow label="Número" value={String(nota.numeroNota).padStart(6, '0')} />
          <InfoRow label="Série" value={nota.serie} />
          <InfoRow label="Número do Lote" value={nota.numeroLote} />
          <InfoRow label="Tipo Documento" value={tipoDocLabel(nota.tipoDocFiscal)} />
          <InfoRow label="Versão" value={nota.versao} />
          <InfoRow label="CNPJ Emitente" value={nota.cnpj && <span className="font-mono">{nota.cnpj}</span>} />
          <InfoRow label="Situação" value={nota.descricaoSituacao} />
          <InfoRow label="Cód. Situação" value={nota.codigoSituacao} />
          <InfoRow label="Data Situação" value={fmtDate(nota.dtSituacao)} />
          <InfoRow label="Criado em" value={fmtDate(nota.dtCriacao)} />
          <InfoRow label="Atualizado em" value={fmtDate(nota.dtAtualizacao)} />
        </CardContent>
      </Card>

      {/* Eventos */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">
            Histórico de Eventos
            {nota.eventos.length > 0 && (
              <Badge variant="secondary" className="ml-2">{nota.eventos.length}</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {nota.eventos.length === 0 ? (
            <p className="text-sm text-muted-foreground py-2">Nenhum evento registrado.</p>
          ) : (
            <div className="mt-2">
              {nota.eventos.map((ev) => (
                <EventoItem
                  key={ev.id}
                  evento={ev}
                  onBaixarXml={() => notaFiscalService.baixarXmlEvento(notaId, ev.id, nota.chaveNota).catch(() =>
                    toast.error('Não foi possível baixar o XML do evento.'))}
                  onImprimir={() => notaFiscalService.abrirCartaCorrecao(notaId, ev.id).catch(() =>
                    toast.error('Não foi possível gerar o impresso da carta de correção.'))}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog descartar rascunho */}
      <Dialog open={descartarDialog} onOpenChange={(o) => { if (!o) setDescartarDialog(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{nota.numeroNota > 0 ? 'Descartar nota não autorizada' : 'Descartar rascunho'}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">
            {nota.numeroNota > 0
              ? `A nota nº ${nota.numeroNota} não foi autorizada pela SEFAZ e será excluída; o número volta a ficar disponível para a próxima nota.`
              : 'O rascunho será excluído. Nada foi enviado à SEFAZ.'}
            {conteudo?.pedido && ` O pedido nº ${conteudo.pedido} volta a poder ser faturado — o estoque e o contas a receber do pedido não são alterados.`}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDescartarDialog(false)}>Voltar</Button>
            <Button variant="destructive" onClick={handleDescartar} disabled={descartando}>
              {descartando ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
              Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmarTransmissaoDialog
        open={confirmarTransmissao}
        producao={nota.isAmbienteProducao}
        avisos={conteudo?.avisos}
        onConfirmar={() => { setConfirmarTransmissao(false); transmitir(notaId); }}
        onFechar={() => setConfirmarTransmissao(false)}
      />

      {/* Dialog: carta de correção / inutilização */}
      <Dialog open={!!evento} onOpenChange={(o) => { if (!o && !enviandoEvento) setEvento(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{eventoCfg.titulo} — NF-e {String(nota.numeroNota).padStart(6, '0')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              {evento === 'cce'
                ? 'A carta de correção não altera valores, quantidades, impostos, destinatário nem datas — para isso a nota é cancelada e emitida de novo. Cada nova carta substitui a anterior.'
                : `O número ${nota.numeroNota} será inutilizado na SEFAZ (${nota.isAmbienteProducao ? 'PRODUÇÃO' : 'homologação'}) e nunca mais poderá ser usado. Use só para desistir de uma nota que não foi autorizada. Esta ação não pode ser desfeita.`}
            </p>
            <div className="space-y-1">
              <Label>{eventoCfg.rotulo}</Label>
              <Textarea rows={4} maxLength={eventoCfg.max}
                value={textoEvento}
                onChange={(e) => setTextoEvento(e.target.value)}
              />
              <p className="text-xs text-muted-foreground text-right">
                {textoEvento.length} / {eventoCfg.max} caracteres (mínimo 15)
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEvento(null)} disabled={enviandoEvento}>Voltar</Button>
            <Button variant={evento === 'cce' ? 'default' : 'destructive'} onClick={handleEvento}
              disabled={enviandoEvento || textoEvento.trim().length < 15}>
              {enviandoEvento && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}
              {eventoCfg.botao}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog cancelamento */}
      <Dialog open={cancelDialog} onOpenChange={(o) => { if (!o) setCancelDialog(false); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cancelar NF-e {String(nota.numeroNota).padStart(6, '0')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Informe a justificativa do cancelamento (mínimo 15 caracteres). Esta ação será enviada à SEFAZ.
              {!conteudo?.pedido && (
                <> Esta nota não veio de um pedido: o cancelamento <strong>não mexe em estoque nem em contas a receber</strong>;
                se a venda foi desfeita, faça esses ajustes à parte.</>
              )}
            </p>
            {conteudo?.pedido && (
              <div className="flex items-start gap-2 rounded-md border p-3">
                <Checkbox id="estornar-pedido" checked={estornarPedido}
                  onCheckedChange={(v) => setEstornarPedido(v === true)} className="mt-0.5" />
                <div className="space-y-1">
                  <Label htmlFor="estornar-pedido" className="cursor-pointer">
                    Estornar também o pedido nº {conteudo.pedido}
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    {estornarPedido
                      ? 'A venda é desfeita: o estoque volta, os títulos a receber e a comissão são cancelados. O pedido estornado não pode ser reaberto. Se houver parcela já recebida, nada é enviado à SEFAZ até o recebimento ser estornado no Financeiro.'
                      : 'Só a nota é cancelada: o pedido, o estoque e o contas a receber continuam como estão. Use quando a nota vai ser emitida de novo.'}
                  </p>
                </div>
              </div>
            )}
            <div className="space-y-1">
              <Label>Justificativa *</Label>
              <Textarea rows={4}
                placeholder="Descreva o motivo do cancelamento..."
                value={justificativa}
                onChange={(e) => setJustificativa(e.target.value)}
              />
              <p className="text-xs text-muted-foreground text-right">{justificativa.length} / 255 caracteres</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialog(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleCancelar}
              disabled={isCancelando || justificativa.trim().length < 15}>
              {isCancelando ? (
                <><RefreshCw className="h-4 w-4 mr-2 animate-spin" />Cancelando...</>
              ) : (
                <><XCircle className="h-4 w-4 mr-2" />Confirmar Cancelamento</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
